/**
 * Atmosphere Experience Page
 * ===========================
 * /experience/colors → "Esplora per Atmosfera"
 * 
 * Premium page using SlabReality Design System.
 * REAL STONE VISUALS filtered by mood/tone.
 * NO flat color blocks - only real material images.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ChevronRight } from 'lucide-react';
import { ATMOSPHERES, getMaterialsByTone } from '../../data/mockMaterials';

const AtmosphereExperiencePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedTone = searchParams.get('tone');
  const [hoveredId, setHoveredId] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  
  // Animation trigger
  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);
  
  // Find selected atmosphere
  const selectedAtmosphere = ATMOSPHERES.find(a => a.id === selectedTone);
  const filteredMaterials = selectedTone ? getMaterialsByTone(selectedTone) : [];
  
  const handleAtmosphereSelect = (atmosphereId) => {
    navigate(`/experience/colors?tone=${atmosphereId}`);
  };
  
  const handleBack = () => {
    if (selectedTone) {
      navigate('/experience/colors');
    } else {
      navigate(-1);
    }
  };
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--sr-bg-primary)' }}>
      {/* Header */}
      <header 
        className="sticky top-0 z-20 backdrop-blur-lg"
        style={{ 
          borderBottom: '1px solid var(--sr-border-subtle)',
          backgroundColor: 'rgba(15, 15, 16, 0.9)'
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBack}
              className="p-2 rounded-lg transition-colors duration-200"
              style={{ color: 'var(--sr-text-muted)' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--sr-bg-hover)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 
                className="text-lg font-medium tracking-tight"
                style={{ 
                  fontFamily: 'var(--sr-font-body)',
                  color: 'var(--sr-text-primary)'
                }}
              >
                {selectedAtmosphere ? selectedAtmosphere.nameIt : 'Esplora per Atmosfera'}
              </h1>
              <p 
                className="text-xs"
                style={{ color: 'var(--sr-text-muted)' }}
              >
                {selectedAtmosphere 
                  ? selectedAtmosphere.moodIt
                  : 'Scopri la pietra naturale attraverso mood e texture'
                }
              </p>
            </div>
          </div>
          
          {/* Breadcrumb */}
          <nav className="hidden md:flex items-center gap-2 text-xs" style={{ color: 'var(--sr-text-muted)' }}>
            <span className="cursor-pointer hover:text-white transition-colors" onClick={() => navigate('/')}>Home</span>
            <ChevronRight className="w-3 h-3" />
            <span className="cursor-pointer hover:text-white transition-colors" onClick={() => navigate('/experience/colors')}>Ispirazione</span>
            {selectedAtmosphere && (
              <>
                <ChevronRight className="w-3 h-3" />
                <span style={{ color: 'var(--sr-accent-gold)' }}>{selectedAtmosphere.nameIt}</span>
              </>
            )}
          </nav>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-6 py-12">
        {!selectedTone ? (
          /* ========================================
             ATMOSPHERE SELECTION GRID
             ======================================== */
          <div className="space-y-16">
            {/* Hero Section */}
            <div 
              className={`text-center max-w-3xl mx-auto transition-all duration-700 ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <p 
                className="text-xs font-medium uppercase tracking-widest mb-4"
                style={{ 
                  fontFamily: 'var(--sr-font-body)',
                  color: 'var(--sr-accent-gold)' 
                }}
              >
                Ispirazione Curata
              </p>
              <h2 
                className="text-4xl md:text-5xl lg:text-6xl mb-6 leading-tight"
                style={{ 
                  fontFamily: 'var(--sr-font-display)',
                  color: 'var(--sr-text-primary)'
                }}
              >
                Trova la Tua
                <span className="block" style={{ color: 'var(--sr-accent-gold)' }}>Atmosfera</span>
              </h2>
              <p 
                className="text-lg leading-relaxed"
                style={{ 
                  fontFamily: 'var(--sr-font-body)',
                  color: 'var(--sr-text-secondary)' 
                }}
              >
                La pietra naturale non è solo colore. È texture, profondità e carattere.
                <br />
                Scegli il mood che parla alla tua visione.
              </p>
            </div>
            
            {/* Atmosphere Grid - 2x3 layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {ATMOSPHERES.map((atmosphere, index) => (
                <button
                  key={atmosphere.id}
                  onClick={() => handleAtmosphereSelect(atmosphere.id)}
                  onMouseEnter={() => setHoveredId(atmosphere.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  className={`group relative aspect-[4/5] rounded-xl overflow-hidden text-left focus:outline-none transition-all duration-500 ${
                    isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                  }`}
                  style={{ 
                    transitionDelay: `${150 + index * 100}ms`,
                    border: hoveredId === atmosphere.id 
                      ? '1px solid var(--sr-accent-gold)' 
                      : '1px solid transparent'
                  }}
                  data-testid={`atmosphere-${atmosphere.id}`}
                >
                  {/* Background Image with zoom effect */}
                  <div 
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-out group-hover:scale-110"
                    style={{ backgroundImage: `url(${atmosphere.image})` }}
                  />
                  
                  {/* Dark Gradient Overlay */}
                  <div 
                    className="absolute inset-0 transition-opacity duration-500"
                    style={{
                      background: `linear-gradient(
                        to top,
                        var(--sr-bg-primary) 0%,
                        rgba(15, 15, 16, 0.7) 40%,
                        rgba(15, 15, 16, 0.3) 70%,
                        transparent 100%
                      )`,
                      opacity: hoveredId === atmosphere.id ? 0.9 : 0.85
                    }}
                  />
                  
                  {/* Content */}
                  <div className="absolute inset-0 p-6 flex flex-col justify-end">
                    {/* Mood Tag - appears on hover */}
                    <p 
                      className="text-xs font-medium tracking-widest uppercase mb-3 transition-all duration-300"
                      style={{ 
                        fontFamily: 'var(--sr-font-body)',
                        color: 'var(--sr-accent-gold)',
                        opacity: hoveredId === atmosphere.id ? 1 : 0,
                        transform: hoveredId === atmosphere.id ? 'translateY(0)' : 'translateY(8px)'
                      }}
                    >
                      {atmosphere.moodIt}
                    </p>
                    
                    {/* Title */}
                    <h3 
                      className="text-2xl md:text-3xl mb-2 transition-colors duration-300"
                      style={{ 
                        fontFamily: 'var(--sr-font-display)',
                        color: hoveredId === atmosphere.id ? 'var(--sr-accent-gold)' : 'var(--sr-text-primary)'
                      }}
                    >
                      {atmosphere.nameIt}
                    </h3>
                    
                    {/* Description */}
                    <p 
                      className="text-sm leading-relaxed mb-4 max-w-sm"
                      style={{ 
                        fontFamily: 'var(--sr-font-body)',
                        color: 'var(--sr-text-secondary)' 
                      }}
                    >
                      {atmosphere.descriptionIt}
                    </p>
                    
                    {/* Arrow indicator + count */}
                    <div 
                      className="flex items-center justify-between transition-all duration-300"
                      style={{
                        opacity: hoveredId === atmosphere.id ? 1 : 0,
                        transform: hoveredId === atmosphere.id ? 'translateX(0)' : 'translateX(-10px)'
                      }}
                    >
                      <div 
                        className="flex items-center gap-2"
                        style={{ color: 'var(--sr-accent-gold)' }}
                      >
                        <span 
                          className="text-sm font-medium uppercase tracking-wider"
                          style={{ fontFamily: 'var(--sr-font-body)' }}
                        >
                          Esplora
                        </span>
                        <ArrowRight className="w-4 h-4" />
                      </div>
                      <span 
                        className="text-xs"
                        style={{ 
                          fontFamily: 'var(--sr-font-body)',
                          color: 'var(--sr-text-muted)' 
                        }}
                      >
                        {atmosphere.count} materiali
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            
            {/* Bottom Quote */}
            <div 
              className="text-center pt-12"
              style={{ borderTop: '1px solid var(--sr-border-subtle)' }}
            >
              <p 
                className="text-sm italic max-w-xl mx-auto"
                style={{ 
                  fontFamily: 'var(--sr-font-display)',
                  color: 'var(--sr-text-muted)' 
                }}
              >
                "Ogni lastra racconta una storia di milioni di anni. 
                Scegli quella che risuona con la tua."
              </p>
            </div>
          </div>
        ) : (
          /* ========================================
             MATERIALS GRID FOR SELECTED ATMOSPHERE
             ======================================== */
          <div className="space-y-10">
            {/* Selected Atmosphere Header */}
            <div 
              className="flex flex-col md:flex-row md:items-end gap-6 pb-8"
              style={{ borderBottom: '1px solid var(--sr-border-subtle)' }}
            >
              {/* Preview Image */}
              <div 
                className="w-full md:w-56 h-40 rounded-xl bg-cover bg-center overflow-hidden"
                style={{ 
                  backgroundImage: `url(${selectedAtmosphere?.image})`,
                  border: '1px solid var(--sr-border-subtle)'
                }}
              />
              
              <div className="flex-1">
                <p 
                  className="text-xs font-medium tracking-widest uppercase mb-2"
                  style={{ 
                    fontFamily: 'var(--sr-font-body)',
                    color: 'var(--sr-accent-gold)' 
                  }}
                >
                  {selectedAtmosphere?.moodIt}
                </p>
                <h2 
                  className="text-3xl md:text-4xl mb-3"
                  style={{ 
                    fontFamily: 'var(--sr-font-display)',
                    color: 'var(--sr-text-primary)'
                  }}
                >
                  {selectedAtmosphere?.nameIt}
                </h2>
                <p 
                  className="max-w-xl"
                  style={{ 
                    fontFamily: 'var(--sr-font-body)',
                    color: 'var(--sr-text-secondary)' 
                  }}
                >
                  {selectedAtmosphere?.descriptionIt}
                </p>
              </div>
              
              {/* Materials count */}
              <div className="text-right">
                <p 
                  className="text-4xl font-light"
                  style={{ 
                    fontFamily: 'var(--sr-font-display)',
                    color: 'var(--sr-accent-gold)' 
                  }}
                >
                  {filteredMaterials.length}
                </p>
                <p 
                  className="text-xs uppercase tracking-widest"
                  style={{ 
                    fontFamily: 'var(--sr-font-body)',
                    color: 'var(--sr-text-muted)' 
                  }}
                >
                  Materiali
                </p>
              </div>
            </div>
            
            {/* Materials Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredMaterials.map((material, index) => (
                <div
                  key={material.id}
                  onClick={() => navigate(`/materials-new/${material.id}`)}
                  className={`group relative aspect-[3/4] rounded-lg overflow-hidden cursor-pointer transition-all duration-500 ${
                    isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ 
                    transitionDelay: `${index * 50}ms`,
                    border: '1px solid var(--sr-border-subtle)'
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--sr-accent-gold)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--sr-border-subtle)'}
                  data-testid={`material-${material.id}`}
                >
                  {/* Image */}
                  <div 
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                    style={{ backgroundImage: `url(${material.main_image_url})` }}
                  />
                  
                  {/* Overlay */}
                  <div 
                    className="absolute inset-0 transition-opacity duration-300"
                    style={{
                      background: 'linear-gradient(to top, var(--sr-bg-primary) 0%, transparent 60%)',
                      opacity: 0.9
                    }}
                  />
                  
                  {/* Content */}
                  <div className="absolute inset-x-0 bottom-0 p-4">
                    <h4 
                      className="text-base font-medium mb-1 group-hover:text-sr-gold transition-colors"
                      style={{ 
                        fontFamily: 'var(--sr-font-display)',
                        color: 'var(--sr-text-primary)'
                      }}
                    >
                      {material.name}
                    </h4>
                    <p 
                      className="text-xs"
                      style={{ 
                        fontFamily: 'var(--sr-font-body)',
                        color: 'var(--sr-text-muted)' 
                      }}
                    >
                      {material.stone_type}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Empty state */}
            {filteredMaterials.length === 0 && (
              <div className="text-center py-20">
                <p style={{ color: 'var(--sr-text-muted)' }}>
                  Nessun materiale trovato per questa atmosfera.
                </p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AtmosphereExperiencePage;
